import React, { useState } from 'react';
import { logAndCaptureException } from 'utils';
import { exists } from 'lib/types';
import LoadingState from 'components/LoadingState';
import api from 'api';
import { useAppDispatch, useAppSelector } from 'redux/hooks';
import { ColumnService } from 'lib/services/directory';
import { selectActiveOrganization, selectUser } from 'redux/auth';
import ToastActions from 'redux/toast';
import RadioButtonGroup from 'lib/components/Checkbox/RadioButtonGroup';
import { ENOTICE_ACCOUNT_ID, DUMMY_ACCOUNT_ID } from '../../../../constants';
import DwollaSettings from './DwollaSettings';
import { StripeDirectDepositOnboardingButton } from './StripeDirectDepositOnboardingButton';
import { StripePayouts } from './StripePayouts';

// add a check about dwolla to render dwolla payouts
export default function PaymentSettings() {
  const activeOrganization = useAppSelector(selectActiveOrganization);
  const user = useAppSelector(selectUser);
  const dispatch = useAppDispatch();
  const [selectedOption, setSelectedOption] = useState<'dwolla' | 'stripe'>(
    'stripe'
  );
  const [stripeOnboardingLoading, setStripeOnboardingLoading] = useState(false);

  if (!exists(activeOrganization) || !exists(user)) {
    return <LoadingState />;
  }

  const handleOptionChange = (value: 'dwolla' | 'stripe') => {
    setSelectedOption(value);
  };

  const organizationData = activeOrganization.data();
  const hasDwollaCustomer = !!organizationData.dwolla?.dwollaCustomer;
  const hasDwollaFundingSource = !!organizationData.dwolla?.dwollaFundingSource;

  const startStripeDirectDepositOnboarding = async () => {
    if (stripeOnboardingLoading) {
      return;
    }

    const logData = {
      organizationId: activeOrganization.id
    };

    setStripeOnboardingLoading(true);

    const result = await api.safePost(
      'payments/create-connected-account-link',
      {
        organizationId: activeOrganization.id,
        returnUrl: `${window.location.origin}/stripe-connect/`,
        refreshUrl: `${window.location.origin}/stripe-connect/`
      }
    );

    if (result.response?.url) {
      window.location.replace(result.response.url);
      // Leave it loading so the button isn't active while redirecting
      return;
    }

    dispatch(
      ToastActions.toastError({
        headerText: 'Unable to start setup',
        bodyText:
          result.error ??
          'There was an error starting direct deposit setup. Please try again.'
      })
    );
    setStripeOnboardingLoading(false);

    logAndCaptureException(
      ColumnService.FINANCIAL_RECONCILIATION,
      new Error(result.error ?? 'Unknown error'),
      'Failed to create Stripe connected account onboarding link',
      { ...logData, error: result.error ?? '' }
    );
  };

  const shouldShowStripeOnboardingButton =
    !hasDwollaFundingSource &&
    (organizationData.accountId === ENOTICE_ACCOUNT_ID ||
      organizationData.accountId === DUMMY_ACCOUNT_ID);

  if (stripeOnboardingLoading) {
    return (
      <div id="payment-settings-wrapper">
        <LoadingState message="Starting direct deposit setup..." />
      </div>
    );
  }

  return (
    <div id="payment-settings-wrapper">
      {shouldShowStripeOnboardingButton ? (
        <StripeDirectDepositOnboardingButton
          isLoading={stripeOnboardingLoading}
          onClick={startStripeDirectDepositOnboarding}
        />
      ) : hasDwollaCustomer && !hasDwollaFundingSource ? (
        <>
          <div className="mt-4 w-full inline-block bg-white border border-column-gray-100 shadow-column-3 mb-4 rounded-lg p-4">
            <div className="flex">
              <RadioButtonGroup
                id="payment-settings-options"
                labelText="Choose your payout view:"
                value={selectedOption}
                onChange={handleOptionChange}
                options={[
                  { labelText: 'Dwolla Payouts', value: 'dwolla' },
                  { labelText: 'Stripe Payouts', value: 'stripe' }
                ]}
              />
            </div>
          </div>
          {selectedOption === 'dwolla' ? (
            <DwollaSettings
              activeOrganization={activeOrganization}
              user={user}
            />
          ) : (
            <StripePayouts />
          )}
        </>
      ) : hasDwollaFundingSource ? (
        <DwollaSettings activeOrganization={activeOrganization} user={user} />
      ) : (
        <StripePayouts />
      )}
    </div>
  );
}
